import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Transfer, message, Tooltip } from 'antd';
import { AppleOutlined, WindowsOutlined, AndroidOutlined, WarningOutlined } from '@ant-design/icons';
import axios from '../../config/axiosInstance';
import { useTheme } from '../../context/ThemeContext';

const DeviceTransferModal = ({ open, onCancel, deviceConfig, onTransferSuccess }) => { 
  const [devices, setDevices] = useState([]);
  const [targetKeys, setTargetKeys] = useState([]);
  const { darkTheme } = useTheme();

  /* Small online/offline bullet */
  const wsDot = (active) => (
      <span style={{ position: 'relative', display: 'inline-block', width: 15, height: 15, marginRight: 6 }}>
      <span // outline
          style={{
            width: 15,
            height: 15,
            borderRadius: '50%',
            backgroundColor: active ? 'rgba(16,185,129,.2)' : 'rgba(255,0,0,.2)',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
      />
      <span // core
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: active ? '#10B981' : '#EF4444',
            position: 'absolute',
            top: 2.5,
            left: 2.5,
          }}
      />
    </span>
  );

  const platformIcons = {
    iOS: <AppleOutlined style={{ fontSize: '16px', marginRight: '8px' }} />,
    MacCatalyst: <AppleOutlined style={{ fontSize: '16px', marginRight: '8px' }} />,
    WinUI: <WindowsOutlined style={{ fontSize: '16px', marginRight: '8px' }} />,
    Android: <AndroidOutlined style={{ fontSize: '16px', marginRight: '8px', color: darkTheme ? '#A4C639' : '#3DDC84', }} />,
  };

  const fetchDevices = useCallback(async () => {
    try {
      const response = await axios.get(`/devices/idiom/${deviceConfig.Type}`);
      const activeDevices = response.data.filter(device => device.isActive);
      setDevices(activeDevices);
      const assignedDeviceIds = activeDevices
        .filter(device => device.DeviceConfigId && device.DeviceConfigId._id === deviceConfig._id)
        .map(device => device._id);
      setTargetKeys(assignedDeviceIds);
    } catch (error) {
      console.error('Error fetching devices:', error);
      message.error('Failed to fetch devices. Please try again.');
    }
  }, [deviceConfig]);

  /* Refresh on opening + every 5 s */
  useEffect(() => {
    if (!deviceConfig || !open) return;

    fetchDevices();
    const id = setInterval(fetchDevices, 5000);
    return () => clearInterval(id); // clean at closing
  }, [deviceConfig, open, fetchDevices]);

  const handleTransferChange = async (newTargetKeys) => {
    const added = newTargetKeys.filter(key => !targetKeys.includes(key));
    const removed = targetKeys.filter(key => !newTargetKeys.includes(key));

    try {
      if (added.length > 0) {
        await axios.put(`/devices/assignDeviceConfig`, {
          deviceIds: added,
          deviceConfigId: deviceConfig._id,
        });
      }

      if (removed.length > 0) {
        await axios.put(`/devices/removeDeviceConfig`, {
          deviceIds: removed,
        });
      }

      message.success('Devices updated successfully.');
      setTargetKeys(newTargetKeys);
      onTransferSuccess();
    } catch (error) {
      console.error('Error updating devices:', error);
      message.error('Error updating devices. Please try again.');
    }
  };

  return (
    <Modal
    title={`Assign Devices - ${deviceConfig?.DeviceConfigName || 'Unknown'}`}
      open={open} 
      onCancel={onCancel}
      footer={null}
      centered
      width={800}
      style={{ top: 20 }}
    >
      <Transfer
        dataSource={devices.map(device => ({ 
          key: device._id, 
          title: (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span>
                {wsDot(device.wsActive)} {platformIcons[device.Platform]} {device.Name}
              </span>
              {!targetKeys.includes(device._id) && device.DeviceConfigId && device.DeviceConfigId._id && (
                <Tooltip title={device.DeviceConfigId.DeviceConfigName} color="gold">
                  <WarningOutlined style={{ fontSize: '16px', color: 'gold' }} />
                </Tooltip>
              )}
            </div>
          ),
          deviceName: device.Name.toLowerCase(),
        }))}
        titles={['Available Devices', 'Assigned Devices']}
        targetKeys={targetKeys}
        onChange={handleTransferChange}
        render={item => item.title}
        showSearch
        filterOption={(inputValue, item) =>
            item.deviceName.includes(inputValue.toLowerCase())
        }
        style={{ width: '100%' }}
        listStyle={{
          width: 350,
          height: 400,
        }}
      />
    </Modal>
  );
};

export default DeviceTransferModal;
